
import React, { createContext, useContext, ReactNode } from 'react';
import { usePopupManager } from '@/hooks/usePopupManager';
import { PopupRenderer } from './PopupRenderer';
import { PopupContent, ScheduledPopup } from '@/types/popup-system';

interface PopupContextType {
  showImmediatePopup: (content: PopupContent) => void;
  addScheduledPopup: (popup: ScheduledPopup) => void;
  activePopupsCount: number;
  popupQueue: ScheduledPopup[];
}

const PopupContext = createContext<PopupContextType | null>(null);

export const usePopupSystem = () => {
  const context = useContext(PopupContext);
  if (!context) {
    throw new Error('usePopupSystem must be used within a PopupProvider');
  }
  return context;
};

interface PopupProviderProps {
  children: ReactNode;
}

export const PopupProvider = ({ children }: PopupProviderProps) => {
  // Add error boundary for popup manager hook
  let popupManager;
  try {
    popupManager = usePopupManager();
  } catch (error) {
    console.error('Error in usePopupManager:', error);
    // Fallback popup manager
    popupManager = {
      activePopups: [],
      dismissPopup: () => {},
      handlePopupAction: () => {},
      addScheduledPopup: () => {},
      showImmediatePopup: () => {},
      popupQueue: []
    };
  }

  const {
    activePopups,
    dismissPopup,
    handlePopupAction,
    addScheduledPopup,
    showImmediatePopup,
    popupQueue
  } = popupManager;

  // Sort popups by priority with safety check
  const sortedPopups = Array.isArray(activePopups) ? [...activePopups].sort((a, b) => {
    const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 };
    return priorityOrder[b.priority] - priorityOrder[a.priority];
  }) : [];

  return (
    <PopupContext.Provider
      value={{
        showImmediatePopup,
        addScheduledPopup,
        activePopupsCount: sortedPopups.length,
        popupQueue: Array.isArray(popupQueue) ? popupQueue : []
      }}
    >
      {children}
      
      {/* Render active popups with error boundary */}
      {sortedPopups.map((popup, index) => {
        try {
          return (
            <PopupRenderer
              key={popup.id}
              content={popup}
              onDismiss={() => dismissPopup(popup.id)}
              onAction={(action) => handlePopupAction(popup.id, action)}
            />
          );
        } catch (error) {
          console.error('Error rendering popup:', popup.id, error);
          return null;
        }
      })}
    </PopupContext.Provider>
  );
};
